import type React from 'react';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { Form, Input, Select, Space } from 'antd';
import type { FieldValues } from 'react-hook-form';
import type { InputProps } from 'antd';
import type { RHFBaseProps, CommonChrome } from './types';
import { DEFAULT_FORM_LAYOUT } from './constants';
import { Flag } from '@elements/Flag';
import {
  PHONE_COUNTRY_CODES,
  parsePhoneValue,
  normalizeNationalNumber,
} from './phoneCountryCodes';

export type ControlledPhoneInputProps<TFieldValues extends FieldValues = FieldValues> =
  RHFBaseProps<TFieldValues> &
    CommonChrome &
    Omit<InputProps, 'value' | 'onChange' | 'onBlur'> & {
      setDialCodeLength?: (length: number) => void;
      /** When provided, the country code Select is controlled by the parent */
      countryCode?: string;
      /** Called when user changes the country code (only relevant when countryCode is provided) */
      onCountryCodeChange?: (dialCode: string) => void;
    };

function getDialCodeDigitLength(dialCode: string): number {
  return (dialCode ?? '').replace(/\D/g, '').length;
}

export function ControlledPhoneInput<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  label,
  required,
  disabled,
  help,
  layout,
  fieldClassName,
  setDialCodeLength,
  countryCode: controlledCountryCode,
  onCountryCodeChange,
  placeholder,
  ...restInputProps
}: ControlledPhoneInputProps<TFieldValues>) {
  const selectOptions = useMemo(
    () =>
      PHONE_COUNTRY_CODES.map(({ iso, dialCode }) => ({
        value: dialCode,
        label: (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Flag name={iso} />
            <span>{dialCode}</span>
          </span>
        ),
      })),
    [],
  );

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange, onBlur }, fieldState }) => {
        const parsed = parsePhoneValue(value);
        const dialCode =
          controlledCountryCode !== undefined && controlledCountryCode !== null
            ? controlledCountryCode
            : parsed.dialCode;
        const nationalNumber = parsed.nationalNumber;

        const handleDialCodeChange = (newDialCode: string) => {
          const normalizedNational = normalizeNationalNumber(nationalNumber);
          const fullPhone = normalizedNational
            ? `${newDialCode}${normalizedNational}`
            : newDialCode;
          onChange(fullPhone);
          onCountryCodeChange?.(newDialCode);
          setDialCodeLength?.(getDialCodeDigitLength(newDialCode));
        };

        const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newNational = normalizeNationalNumber(e.target.value);
          const fullPhone = newNational ? `${dialCode}${newNational}` : dialCode;
          onChange(fullPhone);
        };

        return (
          <Form.Item
            label={label}
            required={required}
            validateStatus={fieldState.error ? 'error' : undefined}
            help={fieldState.error?.message ?? help}
            layout={layout ?? DEFAULT_FORM_LAYOUT}
            className={fieldClassName}
          >
            <Space.Compact style={{ width: '100%' }}>
              <Select
                value={dialCode}
                onChange={handleDialCodeChange}
                options={selectOptions}
                disabled={disabled}
                style={{ width: 120 }}
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) =>
                  String(option?.value ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
              <Input
                {...restInputProps}
                value={nationalNumber}
                onChange={handleNumberChange}
                onBlur={onBlur}
                placeholder={placeholder}
                disabled={disabled}
                style={{ flex: 1, minWidth: 0 }}
              />
            </Space.Compact>
          </Form.Item>
        );
      }}
    />
  );
}
